import type { CampaignWorkspace, CockpitSnapshot } from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly remediation?: string,
  ) {
    super(message);
  }
}

export async function getCockpit(): Promise<CockpitSnapshot> {
  return requestJson<CockpitSnapshot>('/api/v1/cockpit');
}

export async function getCampaignWorkspace(id: string): Promise<CampaignWorkspace> {
  return requestJson<CampaignWorkspace>(`/api/v1/campaigns/${encodeURIComponent(id)}/workflow`);
}

export async function createCampaign(input: {
  label: string;
  platform: 'instantly' | 'edgemail' | 'manual';
  source_method: string | null;
  reason: string;
}): Promise<{ id: string }> {
  return requestJson('/api/v1/campaigns', {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify(input),
  });
}

export async function decideApproval(
  id: string,
  input: { decision: 'approved' | 'rejected'; expected_version: number; reason: string },
): Promise<{ id: string; status: string; version: number }> {
  return requestJson(`/api/v1/approvals/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify(input),
  });
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: { accept: 'application/json', ...init?.headers },
  });
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    remediation?: string;
  } & T;
  if (!response.ok) {
    throw new ApiError(payload.error ?? `Request failed (${response.status})`, response.status, payload.remediation);
  }
  return payload;
}

function writeHeaders(): HeadersInit {
  return {
    'content-type': 'application/json',
    'idempotency-key': crypto.randomUUID(),
  };
}
